/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { AxisLeft, AxisScale } from "@visx/axis";
import { scaleBand, scaleLinear } from "@visx/scale";
import { buildChartTheme } from "@visx/xychart";
import dayjs, { Dayjs } from "dayjs";
import { theme } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { KeyValue } from "~/models";
import {
  convertToPercent,
  formatMoney,
  generateNumericArray,
  isNotNil,
} from "~/tools";
import {
  GenerateDataSeriesProps,
  GetKpiValueColorProps,
  GetValueFromTimePointProps,
  KpiTarget,
  KpiTimePoint,
  TARGET_TYPE,
  TimePoint,
} from "./types";

export function getXScale(xMax: number, data: number[]) {
  return scaleBand<number>({
    range: [1, xMax],
    round: true,
    domain: data?.map((_, index) => index),
    padding: 0.1,
  });
}

export function getYScale(yMax: number, maxDomain = 100) {
  return scaleLinear<number>({
    range: [yMax, 0],
    round: true,
    domain: [0, maxDomain],
  });
}

type Props = {
  yScale: AxisScale;
  yTicks: number;
  fontSize: number;
  tickFormat?: (n: number) => string;
};

export function LeftAxis({
  yScale,
  yTicks,
  fontSize,
  tickFormat = (n) => `${n}%`,
}: Props) {
  return (
    <AxisLeft
      top={0}
      scale={yScale}
      stroke={theme.color.border.focus}
      tickStroke="transparent"
      numTicks={yTicks}
      tickFormat={tickFormat}
      tickLabelProps={() => ({
        fill: theme.color.text.text02,
        fontSize,
        textAnchor: "end",
      })}
    />
  );
}

export const dateFormatter = (number: number): string =>
  `${number < 9 ? "0" : ""}${number + 1}.${dayjs().year()}`;

export function makeCustomTheme(targetColor: string) {
  return buildChartTheme({
    backgroundColor: theme.color.interactive.primary,
    colors: [
      "",
      "",
      targetColor,
      theme.color.text.text01,
      theme.color.text.text01,
      theme.color.support.purple,
    ],
    gridColor: theme.color.border.focus,
    gridColorDark: theme.color.border.focus,
    tickLength: 0,
  });
}

function getValueFromTimePoint({
  date,
  timePoints,
  additionalTimePoints,
  currency,
}: GetValueFromTimePointProps) {
  const timePointIndex = timePoints.findIndex(({ date: timePointDate }) =>
    dayjs(timePointDate).isSame(date, "day")
  );
  const timePoint = timePoints[timePointIndex];
  const additionalTimePoint = additionalTimePoints.find(
    ({ date: timePointDate }) => dayjs(timePointDate).isSame(date, "day")
  );

  if (!timePoint && !additionalTimePoint) {
    return {};
  }

  return {
    value: timePoint ? convertToPercent(Number(timePoint?.value)) : undefined,
    additionalValue: additionalTimePoint
      ? convertToPercent(Number(additionalTimePoint?.value))
      : undefined,
    isEstimated: isTimePointEstimated(timePoints[timePointIndex]),
    isEstimationPadding:
      isTimePointEstimated(timePoints[timePointIndex + 1]) ||
      isTimePointEstimated(timePoints[timePointIndex - 1]),
    additionalFields: [
      ...(timePoint?.additionalFields?.map((field) =>
        mapAdditionalField(field, currency)
      ) ?? []),
    ],
  };
}

function isTimePointEstimated(timePoint: TimePoint | undefined) {
  return isNotNil(
    timePoint?.additionalFields?.find(({ key }) => key === "estimation")
  );
}

type Key =
  | "averageDailyShutdownHours"
  | "dailyMachines"
  | "dailyShutdownHours"
  | "environmentsCount"
  | "totalShutdownHours"
  | "wasteCost";

function isValidKey(key: string): key is Key {
  return [
    "averageDailyShutdownHours",
    "dailyShutdownHours",
    "totalShutdownHours",
    "dailyMachines",
    "environmentsCount",
    "wasteCost",
  ].includes(key);
}

function mapAdditionalField(
  { key, value }: KeyValue,
  currency: string
): KeyValue {
  if (!isValidKey(key)) {
    return { key: "", value: "" };
  }

  switch (key) {
    case "averageDailyShutdownHours":
      return {
        key: "Average Shutdown Time per Machine [h]",
        value: parseFloat(value).toString(),
      };
    case "dailyShutdownHours":
      return {
        key: "Total Daily Shutdown Time [h]",
        value: parseFloat(value).toString(),
      };
    case "totalShutdownHours":
      return {
        key: "Total Monthly Shutdown Time [h]",
        value: parseFloat(value).toString(),
      };
    case "dailyMachines":
      return {
        key: "Number of Machines",
        value,
      };
    case "environmentsCount":
      return {
        key: "Number of Environments",
        value,
      };
    case "wasteCost":
      return {
        key: "Waste",
        value: formatMoney(value, currency),
      };
    default:
      return { key: "", value: "" };
  }
}

function getTargetForDate(date: string, targets: KpiTarget[]) {
  return targets.find(
    ({ startDate: targetStartDate, endDate: targetEndDate }) =>
      dayjs(date).isBetween(targetStartDate, dayjs(targetEndDate), "day", "[]")
  );
}

export function generateDataSeries({
  from,
  to,
  primaryData,
  additionalData,
  targets,
  simplified,
  currency,
}: GenerateDataSeriesProps): KpiTimePoint[] {
  const startDate = dayjs(from);
  const endDate = dayjs(to);

  const timePoints = primaryData.filter((item) =>
    dayjs(item.date).isBetween(startDate, endDate, "day", "[]")
  );

  const additionalTimePoints =
    additionalData?.filter((item) =>
      dayjs(item.date).isBetween(startDate, endDate, "day", "[]")
    ) ?? [];

  const diffMonths = endDate.diff(startDate, "month");

  const isWeeklyData = diffMonths > 3 && simplified;
  const seriesFrequency = isWeeklyData ? "week" : "day";
  const firstDay = isWeeklyData
    ? startDate.startOf("week").add(6, "day")
    : startDate;

  const arraySize = endDate.diff(firstDay, seriesFrequency);

  // Generating empty timeline items on the end of data, used like "margin" after dataset.
  // Its 5% of original time period rounded up.
  const marginSize = Math.ceil(arraySize * 0.05);

  const dates = generateNumericArray(arraySize + 1 + marginSize);

  return dates.map((dayNumber) => {
    const date = firstDay
      .add(dayNumber, seriesFrequency)
      .format(dateFormat.shortDate);

    const isMargin = dayNumber < 0 || dayNumber > arraySize;

    const target = getTargetForDate(date, targets);
    const targetValue = target?.value ? Number(target?.value) : undefined;

    return {
      date,
      targetValue: isMargin ? undefined : targetValue,
      targetName: isMargin ? undefined : target?.name,
      ...(isMargin
        ? {}
        : getValueFromTimePoint({
            date,
            timePoints,
            additionalTimePoints,
            currency,
          })),
    };
  });
}

export function generateKpiTimeTicks(
  dateFrom: string,
  dateTo: string,
  simplified?: boolean
): string[] {
  return simplified
    ? generateSimplifiedTimeTicks(dateFrom, dateTo)
    : generateStandardTimeTicks(dateFrom, dateTo);
}

function generateSimplifiedTimeTicks(dateFrom: string, dateTo: string) {
  const startDate = dayjs(dateFrom);
  const endDate = dayjs(dateTo);
  const diffMonths = endDate.diff(startDate, "month");

  if (diffMonths <= 1) {
    const diffDays = endDate.diff(startDate, "day");
    const dates = generateNumericArray(Math.floor(diffDays / 3));
    return dates.map((dayNumber) =>
      startDate.add(3 * dayNumber, "day").format(dateFormat.shortDate)
    );
  } else if (diffMonths > 3) {
    const diffWeeks = endDate.diff(startDate, "week");
    const dates = generateNumericArray(Math.floor(diffWeeks / 8) + 1);
    return dates.map((weekNumber) =>
      startDate
        .startOf("week")
        .add(6, "day")
        .add(8 * weekNumber, "week")
        .format(dateFormat.shortDate)
    );
  }
  return generateMonthlyTimeTicks(startDate, dateTo, diffMonths);
}

function generateStandardTimeTicks(dateFrom: string, dateTo: string) {
  const startDate = dayjs(dateFrom);
  const endDate = dayjs(dateTo);
  const diffMonths = endDate.diff(startDate, "month");

  if (diffMonths <= 1) {
    const diffDays = endDate.diff(startDate, "day");
    const dates = generateNumericArray(diffDays + 1);
    return dates.map((dayNumber) =>
      startDate.add(dayNumber, "day").format(dateFormat.shortDate)
    );
  } else if (diffMonths <= 3) {
    const diffWeeks = endDate.diff(startDate, "week");
    const dates = generateNumericArray(diffWeeks + 1);
    return dates.map((weekNumber) =>
      startDate.add(weekNumber, "week").format(dateFormat.shortDate)
    );
  }
  return generateMonthlyTimeTicks(startDate, dateTo, diffMonths);
}

function generateMonthlyTimeTicks(
  startDate: Dayjs,
  dateTo: string,
  diffMonths: number
) {
  const dates = generateNumericArray(diffMonths + 1);
  return dates.map((monthNumber) =>
    monthNumber < dates.length - 1
      ? startDate.add(monthNumber, "month").format(dateFormat.shortDate)
      : dateTo
  );
}

export const getTargetColor = (targetType: TARGET_TYPE) =>
  targetType === TARGET_TYPE.positive
    ? theme.colors.statusSuccess[1]
    : theme.colors.statusDanger[1];

export function getKpiValueColor({
  kpiValue,
  targetValue,
  targetType,
  defaultColor = theme.color.text.text04,
}: GetKpiValueColorProps): string {
  if (kpiValue === undefined || targetValue === undefined) {
    return defaultColor;
  }
  if (targetType === TARGET_TYPE.positive) {
    return kpiValue > targetValue ? theme.colors.danger : theme.colors.success;
  }

  return kpiValue < targetValue ? theme.colors.danger : theme.colors.success;
}

export const percentageFormat = (n: number | string) => `${n}%`;

export function getMinMax(values: number[]) {
  return [Math.min(...values), Math.max(...values)];
}

export function getInnerBarPadding(timePoints: number, width: number) {
  const caclulatedPadding = 0.4 + width / 1000 / timePoints;

  if (caclulatedPadding > 0.85) {
    return 0.85;
  }

  if (caclulatedPadding < 0.3) {
    return 0.3;
  }

  return caclulatedPadding;
}

export function getOuterBarPadding(timePoints: number) {
  const paddingOuter = 1 - timePoints / 40;
  return paddingOuter < 0.3 ? 0.3 : paddingOuter;
}

export function calculateMaxDomain(data: number[], hasNoData: boolean) {
  const yMaxValue = Math.max(...data);
  // Multiplicating maximum chart value by 1.1 to extend target area limit 10% above chart or add fixed values if cost is < 0.01
  const maxDomain = yMaxValue < 0.1 ? 0.1 : yMaxValue * 1.1;

  return hasNoData ? 250 : maxDomain;
}

export function calculateMinDomain(data: number[], hasNoData: boolean) {
  const yMinValue = Math.min(...data);

  return hasNoData || yMinValue > 0 ? 0 : yMinValue;
}
